import React from 'react';
import { PaymentPlan } from 'api/types/owner';
import CheckIcon from './images/check.svg';
import { Info } from 'react-feather';
import { Link, Tooltip } from 'ui-kit';

type Feature = {
  text: string | React.ReactNode;
  highlight?: boolean;
};

export const featureData: { [key in PaymentPlan]: Feature[] } = {
  [PaymentPlan.Basic]: [
    {
      text: (
        <span>
          7% Commission + credit card fees{' '}
          <Tooltip
            trigger="click"
            title={
              <p>
                Credit card fees are 2.9% + 30 cents per transaction.{' '}
                <Link
                  href="https://thehostcohelp.zendesk.com/hc/en-us/articles/33876752643981-Merchant-Banking-Fees"
                  style={{ color: 'white' }}
                >
                  Learn more here.
                </Link>
              </p>
            }
          >
            <Info size={18} />
          </Tooltip>
        </span>
      ),
    },
    {
      text: 'Unlimited Stores',
    },
    {
      text: 'Email Notifications + Request & Approve Scheduling',
    },
    {
      text: 'Access to Host Co Local Vendors',
    },
    {
      text: 'In-Store Guest and Host Support',
    },
  ],
  [PaymentPlan.Pro]: [
    {
      text: '0% Commission On $1000/Monthly',
    },
    {
      text: 'Text Notifications (US/CA)',
    },
    {
      text: 'Google Calendar Notifications',
    },
    {
      text: 'Add Your Own Affiliate Programs',
    },
    {
      text: 'Add External Links For Guests: One Hub For All Info',
    },
  ],
  [PaymentPlan.ProPlus]: [
    {
      text: '0% Commission On $5000/Monthly',
    },
    {
      text: 'Multiple Payout Accounts',
    },
    {
      text: 'Co-Hosting',
    },
    {
      text: 'Dedicated Success Manager',
    },
    {
      text: 'Priority Local Vendor Onboarding',
      highlight: true,
    },
  ],
  [PaymentPlan.Enterprise]: [
    {
      text: '0% Commission On All Sales',
    },
    {
      text: 'Custom White-Label Stores + Emails',
    },
    {
      text: 'Branded In-Listing Collateral',
    },
    {
      text: '1:1 Onboarding + Bespoke Store Creation',
    },
    {
      text: 'Up to $1000 Savings (Or More!) vs Basic Plan',
      highlight: true,
    },
  ],
};

const Check = <img src={CheckIcon} />;

export const tableData = {
  pricing: [
    {
      featureName: 'Host Co Commission On Your Sales',
      [PaymentPlan.Basic]: '7%',
      [PaymentPlan.Pro]: '0% on first $1000',
      [PaymentPlan.ProPlus]: '0% on first $5000',
      [PaymentPlan.Enterprise]: '0%',
    },
    {
      featureName: 'Guest Checkout Fee',
      featureIcon: (
        <Info
          size={18}
          onClick={() =>
            window.open(
              'https://thehostcohelp.zendesk.com/hc/en-us/articles/14036133062413-Guest-Fees',
            )
          }
        />
      ),
      [PaymentPlan.Basic]: 'Yes',
      [PaymentPlan.Pro]: 'Yes',
      [PaymentPlan.ProPlus]: 'Yes',
      [PaymentPlan.Enterprise]: 'Yes',
    },
    {
      featureName: (
        <span>
          Credit Card fees covered by Host Co{' '}
          <Tooltip
            trigger="click"
            title={
              <p>
                Credit card fees are 2.9% + 30 cents per transaction.{' '}
                <Link
                  href="https://thehostcohelp.zendesk.com/hc/en-us/articles/33876752643981-Merchant-Banking-Fees"
                  style={{ color: 'white' }}
                >
                  Learn more here.
                </Link>
              </p>
            }
          >
            <Info size={18} />
          </Tooltip>
        </span>
      ),
      [PaymentPlan.Basic]: 'No',
      [PaymentPlan.Pro]: 'Yes',
      [PaymentPlan.ProPlus]: 'Yes',
      [PaymentPlan.Enterprise]: 'Yes',
    },
    {
      featureName: 'Your Local Vendor Earnings',
      [PaymentPlan.Basic]: '5-15%',
      [PaymentPlan.Pro]: '5-15%',
      [PaymentPlan.ProPlus]: '5-15%',
      [PaymentPlan.Enterprise]: '5-15%',
    },
  ],
  localVendorProducts: [
    {
      featureName: 'Access to Local Vendor Products',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Priority Local Vendor Onboarding',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  notifications: [
    {
      featureName: 'Email Notifications',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Slack Notifications',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Text Notifications (US/CA)',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Google Calendar Notifications',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Co-Hosting',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  affiliates: [
    {
      featureName: 'Access to Our Affiliate Partners',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Add Your Own Affiliate Links',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  hostCoStores: [
    {
      featureName: 'Ready-to-Share Stores',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Automated Service Scheduling',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Request & Approve Scheduling',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Hyperlinks to External Instructions',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Guest Instructions After Purchase',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Inventory Management',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Free Customizable Welcome Guide for Guests',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Customizable Storefront',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Add External Links for Guests',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'White Label Branding',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: '',
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  signsAndBranding: [
    {
      featureName: 'Personalized Signage',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'White-Labeled Host Co Stores',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: '',
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'White-Labeled Transaction Emails',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: '',
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  thirdPartyVendors: [
    {
      featureName: 'Automated Notifications to Third-Party Vendors',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Third-Party Vendor Onboarding',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  payouts: [
    {
      featureName: 'Monthly Payouts',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'ACH Payouts',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Multiple Payout Accounts',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  accountingAndReporting: [
    {
      featureName: 'Automated Sales Tax Collection',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Detailed Sales Dashboard',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Exportable Sales Reports',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  guestSupport: [
    {
      featureName: 'Guest Chat Support',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Guest Email Support',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
  ],
  hostSupport: [
    {
      featureName: 'Host Email Support',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Host Chat Support',
      [PaymentPlan.Basic]: Check,
      [PaymentPlan.Pro]: Check,
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'Dedicated Success Manager',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: Check,
      [PaymentPlan.Enterprise]: Check,
    },
    {
      featureName: 'VIP Onboarding',
      [PaymentPlan.Basic]: '',
      [PaymentPlan.Pro]: '',
      [PaymentPlan.ProPlus]: '',
      [PaymentPlan.Enterprise]: Check,
    },
  ],
};

export interface IAddOn {
  planName: string;
  planDescription: string | string[];
  price: number;
  priceInfo: string;
  planInfo: string;
  buttonText: string;
  link: string;
}

export const AddOns: Array<IAddOn> = [
  {
    planName: 'Complete Account & Store Creation',
    planDescription: [
      'Dedicated 2-hour session',
      'Stores launched same day',
      'Complete run through and operations setup',
      'Up to 10 stores created in one session',
    ],
    planInfo: 'credited towards Enterprise annual subscription',
    price: 299,
    priceInfo: 'one-time fee',
    buttonText: 'Get Started',
    link: 'https://buy.stripe.com/6oE03C8G67G9aXKdR9',
  },
  {
    planName: 'Custom White-Label Branding',
    planDescription:
      'Add your logo and company name to your store, emails, subdomain, and custom signature.',
    planInfo: 'credited towards Enterprise annual subscription',
    price: 999,
    priceInfo: 'one-time fee',
    buttonText: 'Learn More',
    link: 'https://www.thehost.co/white-label',
  },
];
